import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  AlertTriangle,
  RefreshCw,
  ExternalLink,
  FlaskConical,
  Layers,
  Copy,
  Check,
  Columns,
} from "lucide-react";
import {
  runsGetRun,
  runsGetRunEvents,
} from "../gen";
import type { RunResponse, RunEventResponse } from "../gen/models";
import { JsonViewer } from "../components/JsonViewer";
import { EventWaterfall } from "../components/EventWaterfall";
import { normalizeAgentType } from "../hooks/useUserDirectory";
import {
  extractRunCost,
  extractRunDuration,
  extractRunMetadata,
  extractRunModel,
  extractRunProvider,
  extractRunTokens,
  extractRunTurns,
  formatCost,
  formatDuration,
  formatTokens,
} from "../components/run/runUtils";

export function RunDetail() {
  const { run_id } = useParams<{ run_id: string }>();

  const [run, setRun] = useState<RunResponse | null>(null);
  const [events, setEvents] = useState<RunEventResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedRunId, setCopiedRunId] = useState(false);
  const [payloadView, setPayloadView] = useState<"split" | "result" | "trigger" | "telemetry">("split");

  const loadRunTrace = async (showLoading = true) => {
    if (!run_id) return;
    if (showLoading) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const [rRes, evRes] = await Promise.all([
        runsGetRun(run_id),
        runsGetRunEvents(run_id),
      ]);
      setRun((rRes as any).data || null);
      setEvents((evRes as any).data || []);
    } catch (err: any) {
      if (showLoading) setError(err?.message || "Failed to load run trace");
    } finally {
      if (showLoading) setLoading(false);
      else setRefreshing(false);
    }
  };

  useEffect(() => {
    loadRunTrace(true);
  }, [run_id]);

  // Polling effect while run is in running state
  useEffect(() => {
    if (!run || run.status !== "running") return;

    let isPolling = false;
    const interval = setInterval(async () => {
      if (isPolling) return;
      isPolling = true;
      try {
        const [rRes, evRes] = await Promise.all([
          runsGetRun(run.id),
          runsGetRunEvents(run.id),
        ]);
        const updatedRun = (rRes as any).data || null;
        const updatedEvents = (evRes as any).data || [];
        setRun((prev) => {
          if (!prev) return updatedRun;
          if (JSON.stringify(prev) === JSON.stringify(updatedRun)) {
            return prev;
          }
          return updatedRun;
        });
        setEvents((prev) => {
          if (JSON.stringify(prev) === JSON.stringify(updatedEvents)) {
            return prev;
          }
          return updatedEvents;
        });
        if (updatedRun && updatedRun.status !== "running") {
          clearInterval(interval);
        }
      } catch {
        // ignore polling errors
      } finally {
        isPolling = false;
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [run?.id, run?.status]);

  const handleCopyRunId = async () => {
    if (!run?.id) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(run.id);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = run.id;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopiedRunId(true);
      setTimeout(() => setCopiedRunId(false), 2000);
    } catch (err) {
      console.error("Failed to copy run ID:", err);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800";
      case "running":
        return "bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800 animate-pulse";
      case "failed":
        return "bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800";
      case "simulation":
        return "bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800";
      case "blocked":
        return "bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800";
      default:
        return "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border-stone-200 dark:border-stone-700";
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-12 text-center text-stone-400 text-xs">
        Loading execution trace & step waterfall...
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="p-8 text-center bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 text-rose-600 dark:text-rose-400 text-xs">
          {error || "Run not found"}
        </div>
      </div>
    );
  }

  const t: any = run.telemetry || {};
  const duration = extractRunDuration(run);
  const cost = extractRunCost(run);
  const model = extractRunModel(run);
  const provider = extractRunProvider(run);
  const turns = extractRunTurns(run);
  const { inTok, outTok, total: totalTokens, cached } = extractRunTokens(run);

  const meta = extractRunMetadata(run);
  const isTestRun = meta.isTestRun;
  const testRunId = meta.testRunId;
  const snapshotId = meta.snapshotId;
  const snapshotName = meta.snapshotName;
  const errorMessage =
    meta.errorMessage ||
    (run.result_payload as any)?.error ||
    (run.result_payload as any)?.message;

  const temporalWorkflowId =
    t.temporal_workflow_id ||
    (run.state as any)?.temporal_workflow_id;
  const temporalRunId =
    t.temporal_run_id ||
    (run.state as any)?.temporal_run_id;

  return (
    <div className="w-full max-w-7xl mx-auto space-y-5 min-w-0">
      {/* Test Run Scoping Banner */}
      {isTestRun && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 px-4 py-2.5 rounded-xl border border-indigo-200/80 dark:border-indigo-800/60 bg-indigo-50/70 dark:bg-indigo-950/30 text-xs text-indigo-950 dark:text-indigo-200 shadow-2xs">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-6 h-6 rounded-md bg-indigo-100 dark:bg-indigo-900/60 text-indigo-700 dark:text-indigo-300 flex items-center justify-center shrink-0">
              <FlaskConical className="w-3.5 h-3.5" />
            </div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 min-w-0">
              <span className="font-bold text-indigo-950 dark:text-indigo-100">Automated Test Run</span>
              {testRunId && (
                <span className="font-mono text-[11px] text-indigo-600 dark:text-indigo-400">
                  ID: {String(testRunId).slice(0, 8)}
                </span>
              )}
              <span className="text-indigo-300 dark:text-indigo-700 hidden sm:inline">•</span>
              <span className="text-indigo-800/90 dark:text-indigo-300/90 text-[11px] truncate">
                Snapshot:{" "}
                {snapshotId ? (
                  <Link
                    to={`/snapshots?snapshot_id=${snapshotId}`}
                    className="font-semibold underline hover:text-indigo-600 dark:hover:text-indigo-200"
                  >
                    {snapshotName || String(snapshotId).slice(0, 8)}
                  </Link>
                ) : (
                  <strong>{snapshotName || "Active Baseline"}</strong>
                )}
              </span>
            </div>
          </div>
          {testRunId && (
            <Link
              to={`/test-runs?test_run_id=${testRunId}`}
              className="px-2.5 py-1 bg-white dark:bg-stone-800 hover:bg-stone-100 dark:hover:bg-stone-700 text-stone-700 dark:text-stone-300 border border-stone-200 dark:border-stone-700 rounded-lg text-xs font-medium inline-flex items-center gap-1 transition shrink-0 self-start sm:self-auto"
            >
              <span>View Test Run</span>
              <ExternalLink className="w-3 h-3" />
            </Link>
          )}
        </div>
      )}

      {/* Execution Failure Banner */}
      {run.status === "failed" && (
        <div className="p-3.5 rounded-xl border border-rose-200 dark:border-rose-900/60 bg-rose-50/80 dark:bg-rose-950/40 text-xs text-rose-900 dark:text-rose-200 flex items-start gap-2.5 shadow-2xs">
          <div className="w-6 h-6 rounded-md bg-rose-100 dark:bg-rose-900/60 text-rose-700 dark:text-rose-300 flex items-center justify-center shrink-0 mt-0.5">
            <AlertTriangle className="w-3.5 h-3.5" />
          </div>
          <div className="space-y-1 min-w-0 flex-1">
            <div className="font-bold text-rose-950 dark:text-rose-100 flex items-center gap-2">
              <span>Execution Failed</span>
              <span className="text-[10px] uppercase font-mono px-1.5 py-0.2 rounded bg-rose-200/60 dark:bg-rose-800/60 text-rose-800 dark:text-rose-200">
                Failed
              </span>
            </div>
            <div className="text-rose-800/90 dark:text-rose-300/90 text-[11px] leading-relaxed break-words font-mono">
              {String(errorMessage || "The agent execution terminated with an error.")}
            </div>
            {run.batch_id && (
              <div className="pt-0.5">
                <Link
                  to={`/batches/${run.batch_id}`}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 dark:text-rose-300 hover:text-rose-900 dark:hover:text-rose-100 underline"
                >
                  <Layers className="w-3.5 h-3.5" />
                  <span>Go to Feedback Batch ({run.batch_id.slice(0, 8)}...) &rarr;</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Overview Header Card with Integrated Metrics */}
      <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-5 shadow-2xs space-y-4">
        {/* Title, Badges & Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2.5 min-w-0">
            <span className="px-2.5 py-0.5 rounded-md text-xs font-bold uppercase tracking-wider bg-stone-900 dark:bg-stone-800 text-white shrink-0">
              {normalizeAgentType(run.agent_type)}
            </span>
            <div className="flex items-center gap-1.5 min-w-0">
              <h1 className="text-base sm:text-lg font-bold text-stone-900 dark:text-stone-100 font-mono tracking-tight truncate">
                {run.id}
              </h1>
              <button
                type="button"
                onClick={handleCopyRunId}
                className="p-1 rounded text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition shrink-0"
                title={copiedRunId ? "Copied ID!" : "Copy Run ID"}
                aria-label="Copy Run ID"
              >
                {copiedRunId ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <span
              className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize border shrink-0 ${getStatusBadgeClass(
                run.status
              )}`}
            >
              {run.status}
            </span>
            {isTestRun && (
              <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 flex items-center gap-1 shrink-0">
                <FlaskConical className="w-3 h-3" />
                Test Run
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {temporalWorkflowId && (
              <a
                href={`https://temporal.silvestre.cr/namespaces/default/workflows/${encodeURIComponent(
                  temporalWorkflowId
                )}${temporalRunId ? `/${encodeURIComponent(temporalRunId)}` : ""}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-lg text-sky-700 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-950/40 border border-sky-200 dark:border-sky-800/60 transition"
                title="Open execution in Temporal Web UI"
              >
                <span>Temporal</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <button
              type="button"
              onClick={() => loadRunTrace(false)}
              disabled={refreshing}
              className="p-1.5 rounded-lg border border-stone-200 dark:border-stone-800 text-stone-500 hover:text-stone-900 dark:text-stone-400 dark:hover:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
              title="Refresh run"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Metadata & Key Metrics Bar */}
        <div className="flex flex-wrap items-center justify-between gap-y-2 gap-x-4 pt-3 border-t border-stone-100 dark:border-stone-800 text-xs text-stone-400 dark:text-stone-500">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 min-w-0">
            <span>
              Entry Point:{" "}
              <code className="font-mono text-stone-700 dark:text-stone-300 font-semibold">
                {run.entry_point}
              </code>
            </span>
            <span>•</span>
            <span>
              Started:{" "}
              <span className="text-stone-700 dark:text-stone-300 font-medium">
                {new Date(run.started_at).toLocaleString()}
              </span>
            </span>
            {run.completed_at && (
              <>
                <span>•</span>
                <span>
                  Ended:{" "}
                  <span className="text-stone-700 dark:text-stone-300 font-medium">
                    {new Date(run.completed_at).toLocaleTimeString()}
                  </span>
                </span>
              </>
            )}
            <span>•</span>
            <span>
              Latency:{" "}
              <span className="font-mono text-stone-700 dark:text-stone-300 font-medium">
                {duration !== undefined && duration > 0 ? (formatDuration(duration) || `${duration}ms`) : "-"}
              </span>
            </span>
            <span>•</span>
            <span>
              Total Tokens:{" "}
              <span className="font-mono text-stone-700 dark:text-stone-300 font-medium">
                {totalTokens ? totalTokens.toLocaleString() : "-"}
              </span>
              {Boolean(inTok || outTok) && (
                <span className="text-stone-400 dark:text-stone-500 font-mono ml-1">
                  ({formatTokens(inTok)} in • {formatTokens(outTok)} out)
                </span>
              )}
            </span>
            {Boolean(cached > 0) && (
              <>
                <span>•</span>
                <span>
                  Cached:{" "}
                  <span className="font-mono text-indigo-600 dark:text-indigo-400 font-medium">
                    {cached.toLocaleString()}
                  </span>
                  {Boolean(inTok > 0) && (
                    <span className="text-stone-400 dark:text-stone-500 font-mono ml-1">
                      ({Math.round((cached / inTok) * 100)}%)
                    </span>
                  )}
                </span>
              </>
            )}
            <span>•</span>
            <span>
              Est. Cost:{" "}
              <span className="font-mono text-emerald-600 dark:text-emerald-400 font-medium">
                {formatCost(cost)}
              </span>
            </span>
            {Boolean(model) && (
              <>
                <span>•</span>
                <span>
                  Model:{" "}
                  <span className="font-mono text-stone-700 dark:text-stone-300 font-medium">
                    {model}
                  </span>
                  {Boolean(provider) && (
                    <span className="text-amber-700 dark:text-amber-400 font-mono ml-1">
                      ({provider})
                    </span>
                  )}
                </span>
              </>
            )}
            <span>•</span>
            <span>
              Execution:{" "}
              <span className="font-mono text-stone-700 dark:text-stone-300 font-medium">
                {turns ? `${turns} ${turns === 1 ? "turn" : "turns"}` : `${events.length} steps`}
              </span>
              {Boolean(turns && events.length > 0) && (
                <span className="text-stone-400 dark:text-stone-500 font-mono ml-1">
                  ({events.length} {events.length === 1 ? "event" : "events"})
                </span>
              )}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-3 shrink-0">
            {run.thread_id && (
              <Link
                to={`/threads/${run.thread_id}`}
                className="inline-flex items-center gap-1 font-mono text-emerald-700 dark:text-emerald-400 hover:underline"
                title="View conversation thread"
              >
                <span>Thread: {run.thread_id.slice(0, 10)}...</span>
                <ExternalLink className="w-3 h-3" />
              </Link>
            )}
            {run.batch_id && (
              <Link
                to={`/batches/${run.batch_id}`}
                className="inline-flex items-center gap-1 font-mono text-indigo-700 dark:text-indigo-400 hover:underline"
                title="View feedback batch"
              >
                <span>Batch: {run.batch_id.slice(0, 10)}...</span>
                <ExternalLink className="w-3 h-3" />
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Payloads & Diagnostics Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="inline-flex rounded-lg border border-stone-200 dark:border-stone-800 p-0.5 bg-stone-100/80 dark:bg-stone-900/80 text-xs">
            <button
              type="button"
              onClick={() => setPayloadView("split")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition ${
                payloadView === "split"
                  ? "bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 shadow-2xs font-semibold"
                  : "text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200"
              }`}
            >
              <Columns className="w-3.5 h-3.5" />
              <span>Input & Output</span>
            </button>
            <button
              type="button"
              onClick={() => setPayloadView("result")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition ${
                payloadView === "result"
                  ? "bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 shadow-2xs font-semibold"
                  : "text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200"
              }`}
            >
              <span>Result Payload</span>
            </button>
            <button
              type="button"
              onClick={() => setPayloadView("trigger")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition ${
                payloadView === "trigger"
                  ? "bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 shadow-2xs font-semibold"
                  : "text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200"
              }`}
            >
              <span>Trigger Payload</span>
            </button>
            <button
              type="button"
              onClick={() => setPayloadView("telemetry")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition ${
                payloadView === "telemetry"
                  ? "bg-white dark:bg-stone-800 text-stone-900 dark:text-stone-100 shadow-2xs font-semibold"
                  : "text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200"
              }`}
            >
              <span>Raw Telemetry</span>
            </button>
          </div>
        </div>

        {payloadView === "split" ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <JsonViewer
              title="Trigger Payload (Input)"
              data={run.trigger_payload}
            />
            <JsonViewer
              title="Result Payload (Output)"
              data={run.result_payload || {}}
            />
          </div>
        ) : payloadView === "result" ? (
          <JsonViewer
            title="Result Payload (Output)"
            data={run.result_payload || {}}
          />
        ) : payloadView === "trigger" ? (
          <JsonViewer
            title="Trigger Payload (Input)"
            data={run.trigger_payload}
          />
        ) : (
          <JsonViewer
            title="Raw Telemetry"
            data={run.telemetry || {}}
          />
        )}
      </div>

      {/* Main Waterfall Timeline */}
      <div className="space-y-6">
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-2xs">
          <EventWaterfall events={events} />
        </div>
      </div>
    </div>
  );
}
